// Token persistence behind an injectable key/value store (localStorage in the
// browser, an in-memory map in tests). No React, no direct localStorage coupling.
export interface Session {
    accessToken: string;
    refreshToken: string;
    expiresAt?: number;
    activeOrganizationId?: string;
}

export interface KeyValueStore {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

const SESSION_KEY = "ede.session";

export class SessionStore {
    constructor(private readonly store: KeyValueStore) {}

    get(): Session | null {
        const raw = this.store.getItem(SESSION_KEY);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as Session;
        } catch {
            return null;
        }
    }

    set(session: Session): void {
        this.store.setItem(SESSION_KEY, JSON.stringify(session));
    }

    patch(partial: Partial<Session>): void {
        const current = this.get();
        if (!current) return;
        this.set({ ...current, ...partial });
    }

    clear(): void {
        this.store.removeItem(SESSION_KEY);
    }

    getAccessToken(): string | null {
        return this.get()?.accessToken ?? null;
    }

    getRefreshToken(): string | null {
        return this.get()?.refreshToken ?? null;
    }

    // Per-user visible/allowed organization scope (routing.md contract).
    getVisibleOrgs(userId: string): string[] | null {
        const raw = this.store.getItem(`ede.visibleOrgs.${userId}`);
        if (!raw) return null;
        try {
            const value = JSON.parse(raw);
            return Array.isArray(value) ? value.map(String) : null;
        } catch {
            return null;
        }
    }

    setVisibleOrgs(userId: string, ids: string[]): void {
        this.store.setItem(`ede.visibleOrgs.${userId}`, JSON.stringify(ids));
    }
}

// Browser localStorage adapter (guarded for non-DOM runtimes).
export function browserStore(): KeyValueStore {
    if (typeof localStorage !== "undefined") return localStorage;
    const map = new Map<string, string>();
    return {
        getItem: (k) => map.get(k) ?? null,
        setItem: (k, v) => void map.set(k, v),
        removeItem: (k) => void map.delete(k),
    };
}
